import type { ExtractOutput } from '@precon-tools-suite-by-shared/precon-graphql-types';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';
import { joinNames, resolveName } from '../helpers/node-to-name';
import type { NodeToComposedNameFragment } from './composedNames.generated';

export function useNodesToComposedNames(
    nodes: MaybeRefOrGetter<
        Array<NodeToComposedNameFragment | null | undefined>
    >,
    short: MaybeRefOrGetter<boolean> = false
) {
    return computed(() =>
        toValue(nodes)?.map((x) => fragmentToComposedName(x, toValue(short)))
    );
}

export function useNodeToComposedName(
    node: MaybeRefOrGetter<NodeToComposedNameFragment | null | undefined>,
    short: MaybeRefOrGetter<boolean> = false
) {
    return computed(() =>
        fragmentToComposedName(toValue(node), toValue(short))
    );
}

export function useFragmentToComposedName() {
    return { fragmentToComposedName };
}

function fragmentToComposedName(
    node: NodeToComposedNameFragment | null | undefined,
    short = false
): string | undefined {
    if (!node) return undefined;
    switch (node.__typename) {
        case 'SupplierBranch':
            return supplierBranchToName(node, short);
        case 'Risk':
            return riskToName(node, short);
        case 'AnalysisReportEntry':
            return analysisEntryToName(node, short);
        default:
            return resolveName(node, short);
    }
}

function analysisEntryToName(
    node: ExtractOutput<NodeToComposedNameFragment, 'AnalysisReportEntry'>,
    short = false
) {
    const threat = resolveName(node?.risk?.threat?.name, short);
    const product = resolveName(node?.product?.name, short);

    let supplier: string;
    if (node.supplierBranch) {
        supplier = supplierBranchToName(node?.supplierBranch, short);
    } else {
        supplier = resolveName();
    }

    return `${threat} in ${product} geleverd door ${supplier}`;
}

function supplierBranchToName(
    node: ExtractOutput<NodeToComposedNameFragment, 'SupplierBranch'>,
    short = false
) {
    return `${resolveName(node?.supplier?.name, short)} - ${resolveName(
        node?.name,
        short
    )}`;
}

function riskToName(
    node: ExtractOutput<NodeToComposedNameFragment, 'Risk'>,
    short = false
) {
    const categories = node?.productCategories?.edges?.map((x) => x?.node);
    const threat = resolveName(node?.threat?.name, short);

    if (
        categories === null ||
        categories === undefined ||
        categories.length === 0
    )
        return threat.toString();

    const categoryNames = categories.map((x) => resolveName(x?.name, short));
    const joinedCategories = joinNames(categoryNames);

    return `${threat} in ${joinedCategories}`;
}
